import classNames from 'classnames';
import { useState } from 'react';
import { ErrorMessage } from 'formik';

import { getCategoryName } from '../../../utils/common/category';
import useCommonStyles from '../commonStyles';
import TextLabel from '../TextLabel/TextLabel';
import { ReactComponent as WarningIcon } from '../../../svgIcons/warning.svg';
import { ReactComponent as RequiredErrorIcon } from '../../../svgIcons/required-error.svg';
import { MAX_CHRS, TEXT_ROWS } from '../../../constants';
import analytics from '../../../services/analytics';
import tooltipStyles from '../tooltipStyles';

import useStyles from './styles';
import { IGenericInputAreaProps } from './types';

function GenericInputArea({
  name,
  formProps: props,
  placeholder,
  textLabel,
  hasErrorValidation,
  selectedCategory,
  warningMessage,
  required,
  information,
  analyticsName,
  tooltipProps,
  isOptional,
  labelDescription,
  rows = TEXT_ROWS,
}: IGenericInputAreaProps) {
  const commonStyles = useCommonStyles();
  const tooltipClasses = tooltipStyles({ tooltipProps });
  const classes = useStyles();
  const [words, setWords] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [changed, setChanged] = useState(false);

  const inputClassName = classNames(
    commonStyles.inputClassName,
    classes.inputClassName,
    showWarning ? commonStyles.inputWarning : null,
    hasErrorValidation &&
      props.errors[`${name}`] &&
      props.touched[`${name}`] &&
      commonStyles.inputError,
  );

  const handleWarning = () => {
    if (words > 0 && words < 100) setShowWarning(true);
    else setShowWarning(false);
  };

  const handleOnChange = (e) => {
    setWords(e.target.value.length);
    setChanged(true);
    e.target.value.length == 0 && setShowWarning(false);
    props.handleChange(e);
  };

  const handleBlur = (e) => {
    props.handleBlur(e);
    changed &&
      analytics.textboxType({
        text_box_name: analyticsName,
        category_name: getCategoryName(selectedCategory),
        page_path: window.location.pathname,
      });
    warningMessage != null && handleWarning();
  };

  const handleClick = () => {
    analytics.textBoxClick({
      text_box_name: analyticsName,
      category_name: getCategoryName(selectedCategory),
      page_path: window.location.pathname,
    });
  };

  return (
    <>
      <div className={classes.labelContainer}>
        <div className={classes.left}>
          <TextLabel
            tooltipClassName={tooltipClasses.tooltip}
            arrowClassName={tooltipClasses.tooltipArrow}
            required={required}
            htmlFor={name}
            label={textLabel}
            infoText={information}
          />
          {labelDescription && (
            <span className={classNames(classes.wordLabel, classes.noMargin)}>
              {labelDescription}
            </span>
          )}
        </div>
        {isOptional && <span className={classes.wordLabel}>Optional</span>}
      </div>
      <textarea
        onClick={handleClick}
        rows={rows}
        className={inputClassName}
        name={name}
        placeholder={placeholder}
        value={props.values[`${name}`]}
        onBlur={handleBlur}
        onChange={handleOnChange}
        maxLength={MAX_CHRS}
      />
      <div className={classes.container}>
        {showWarning && (
          <span className={classNames(commonStyles.messageText, commonStyles.warning)}>
            <WarningIcon className={commonStyles.messageIcon} />
            {warningMessage}
          </span>
        )}

        {hasErrorValidation && (
          <ErrorMessage
            name={name}
            render={(msg) => (
              <div className={commonStyles.messageText}>
                <RequiredErrorIcon className={commonStyles.messageIcon} />
                {msg}
              </div>
            )}
          />
        )}

        <span className={classes.wordLabel}>
          {words}/{MAX_CHRS}
        </span>
      </div>
    </>
  );
}
export default GenericInputArea;
